import { useEffect, useState, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { supabase } from '../../supabaseClient'
import { formatDisplayName } from '../../lib/visitorContext'
import ShareLinkBuilder from './ShareLinkBuilder'
import './Dashboard.css'

const DEFAULT_CHECKLIST = [
  { id: 'researched', label: 'Researched company / role', done: false },
  { id: 'first_msg', label: 'Sent first outreach message', done: false },
  { id: 'got_reply', label: 'Got a reply', done: false },
  { id: 'shared_link', label: 'Shared /hi link or portfolio', done: false },
  { id: 'followed_up', label: 'Sent follow-up', done: false },
  { id: 'meeting', label: 'Call / meeting booked', done: false },
]

const SOURCE_EMOJI = {
  direct: '🔗',
  twitter: '𝕏',
  linkedin: '💼',
  github: '🐙',
  google: '🔍',
  facebook: '📘',
  instagram: '📷',
  reddit: '🤖',
  youtube: '▶️',
  whatsapp: '💬',
  telegram: '✈️',
}

const EVENT_COLOR = {
  page_view: '#60a5fa',
  resume_click: '#34d399',
  blog_open: '#f59e0b',
  blog_scroll_depth: '#fbbf24',
  blog_read_time: '#fcd34d',
  project_open: '#a78bfa',
  contact_click: '#f87171',
  social_click: '#38bdf8',
  visitor_identified: '#f472b6',
  experience_tab_click: '#fb923c',
  featured_project_modal_open: '#c084fc',
  why_hire_me_open: '#4ade80',
  session_close: '#94a3b8',
}

const FILTER_TYPES = [
  'all',
  'visitor_identified',
  'page_view',
  'resume_click',
  'contact_click',
  'social_click',
  'project_open',
  'blog_open',
  'blog_scroll_depth',
  'blog_read_time',
  'session_close',
]

const EVENT_COLUMNS = [
  'seen',
  'created_at',
  'event_type',
  'visitor_name',
  'page',
  'target',
  'duration_ms',
  'click_location',
  'company',
  'project_title',
  'source',
  'device',
  'timezone',
  'scroll_depth',
  'is_founder_route',
  'session_id',
]

const PAGE_SIZE = 25

const MODAL_EVENT_COLUMNS = [
  'seen',
  'created_at',
  'event_type',
  'page',
  'target',
  'duration_ms',
  'timezone',
  'scroll_depth',
  'source',
  'device',
]

const metaTimezone = (row) => row?.metadata?.timezone || null
const timezoneLabel = (timezone) => timezone === 'UTC' ? 'UTC (browser)' : timezone || 'Timezone unknown'
const locationLabel = (city, country) => [city, country].filter(Boolean).join(', ') || 'Unknown'
const browserLabel = (userAgent) => {
  const ua = userAgent || ''
  if (/Edg\/|EdgiOS/.test(ua)) return 'Edge'
  if (/OPR\/|OPiOS|Opera/.test(ua)) return 'Opera'
  if (/Firefox\/|FxiOS/.test(ua)) return 'Firefox'
  if (/SamsungBrowser/.test(ua)) return 'Samsung Internet'
  if (/Chrome\/|CriOS/.test(ua)) return 'Chrome'
  if (/Safari\//.test(ua)) return 'Safari'
  return 'Unknown browser'
}
const osLabel = (userAgent) => {
  const ua = userAgent || ''
  if (/Windows/.test(ua)) return 'Windows'
  if (/iPhone|iPad|iPod/.test(ua)) return 'iOS'
  if (/Android/.test(ua)) return 'Android'
  if (/Mac OS X|Macintosh/.test(ua)) return 'macOS'
  if (/Linux/.test(ua)) return 'Linux'
  return 'Unknown OS'
}
const metaScrollDepth = (row) => {
  const depth = row?.metadata?.scroll_depth
  if (depth == null || depth === '') return null
  return `${depth}%`
}

const fmt = (ts) => {
  const d = new Date(ts)
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const fmtDuration = (ms) => {
  if (ms == null || ms === '' || Number.isNaN(Number(ms))) return '—'
  const total = Math.max(0, Math.round(Number(ms) / 1000))
  if (total < 60) return `${total}s`
  const m = Math.floor(total / 60)
  const s = total % 60
  if (m < 60) return s ? `${m}m ${s}s` : `${m}m`
  const h = Math.floor(m / 60)
  const rm = m % 60
  return rm ? `${h}h ${rm}m` : `${h}h`
}

const sourceLabel = (s) => `${SOURCE_EMOJI[s] || '🌐'} ${s}`

const countByType = async (eventType) => {
  const { count } = await supabase
    .from('analytics_events')
    .select('*', { count: 'exact', head: true })
    .eq('event_type', eventType)
  return count || 0
}

const mergeChecklist = (stored) => {
  const map = new Map((stored || []).map((item) => [item.id, item]))
  return DEFAULT_CHECKLIST.map((item) => ({
    ...item,
    done: Boolean(map.get(item.id)?.done),
  }))
}

const applyVisitorEventFilter = (query, visitor) => {
  if (visitor.visitor_name) return query.eq('visitor_name', visitor.visitor_name)
  if (visitor.visitor_id) return query.eq('visitor_id', visitor.visitor_id)
  return query.eq('session_id', visitor.visitor_key)
}

export default function Dashboard() {
  const [authed, setAuthed] = useState(false)
  const [authReady, setAuthReady] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [authError, setAuthError] = useState('')
  const [authLoading, setAuthLoading] = useState(false)

  const [tab, setTab] = useState('visitors')
  const [visitorKind, setVisitorKind] = useState('all')
  const [visitorStatus, setVisitorStatus] = useState('active')
  const [visitorActionId, setVisitorActionId] = useState(null)
  const [visitorError, setVisitorError] = useState('')
  const [selectedAnonymousKeys, setSelectedAnonymousKeys] = useState(() => new Set())
  const [bulkFounderName, setBulkFounderName] = useState('')
  const [visitors, setVisitors] = useState([])
  const [followups, setFollowups] = useState({})
  const [events, setEvents] = useState([])
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(false)
  const [filterType, setFilterType] = useState('all')
  const [filterSeen, setFilterSeen] = useState('unseen')
  const [filterVisitor, setFilterVisitor] = useState(null)
  const [page, setPage] = useState(0)
  const [total, setTotal] = useState(0)
  const [marking, setMarking] = useState(false)

  const [eventsModalVisitor, setEventsModalVisitor] = useState(null)
  const [assignVisitorName, setAssignVisitorName] = useState('')
  const [modalEvents, setModalEvents] = useState([])
  const [modalLoading, setModalLoading] = useState(false)
  const [openSessionIds, setOpenSessionIds] = useState(() => new Set())

  const [checklistVisitor, setChecklistVisitor] = useState(null)

  useEffect(() => {
    try {
      localStorage.removeItem('_dash_auth')
    } catch (_) {}

    if (!supabase) {
      setAuthReady(true)
      return undefined
    }

    let mounted = true

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return
      setAuthed(Boolean(data.session))
      setAuthReady(true)
    })

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setAuthed(Boolean(session))
      setAuthReady(true)
    })

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [])

  const handleLogin = async (e) => {
    e.preventDefault()
    if (!supabase) {
      setAuthError('Supabase is not configured')
      return
    }

    setAuthLoading(true)
    setAuthError('')

    const { error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    })

    setAuthLoading(false)

    if (error) {
      setAuthError(error.message || 'Invalid email or password')
      return
    }

    setAuthed(true)
  }

  const handleLogout = async () => {
    if (supabase) await supabase.auth.signOut()
    setAuthed(false)
    setPassword('')
  }

  const fetchFollowups = useCallback(async () => {
    if (!supabase) return
    const { data, error } = await supabase.from('founder_followups').select('*')
    if (error || !data) return
    const map = {}
    data.forEach((row) => {
      map[row.visitor_name] = row
    })
    setFollowups(map)
  }, [])

  const fetchVisitors = useCallback(async () => {
    if (!supabase) return
    const { data, error } = await supabase
      .from('analytics_visitor_summary')
      .select('*')
      .order('last_seen', { ascending: false })

    if (!error) setVisitors(data || [])
  }, [])

  const fetchStats = useCallback(async () => {
    if (!supabase) return

    const [
      { count: total_events },
      { count: unseen },
      resume_clicks,
      page_views,
      contact_clicks,
      project_opens,
      blog_opens,
    ] = await Promise.all([
      supabase.from('analytics_events').select('*', { count: 'exact', head: true }),
      supabase.from('analytics_events').select('*', { count: 'exact', head: true }).eq('seen', false),
      countByType('resume_click'),
      countByType('page_view'),
      countByType('contact_click'),
      countByType('project_open'),
      countByType('blog_open'),
    ])

    const { data: visitorRows } = await supabase
      .from('analytics_visitor_summary')
      .select('visitor_key, visitor_name')

    const named_visitors = (visitorRows || []).filter((v) => v.visitor_name).length
    const total_visitors = visitorRows?.length || 0

    setStats({
      total_events: total_events || 0,
      unseen: unseen || 0,
      resume_clicks,
      page_views,
      contact_clicks,
      project_opens,
      blog_opens,
      named_visitors,
      total_visitors,
      anonymous_visitors: total_visitors - named_visitors,
    })
  }, [])

  const fetchEvents = useCallback(async () => {
    if (!supabase) return
    setLoading(true)

    let query = supabase
      .from('analytics_events')
      .select(
        'id, seen, created_at, event_type, visitor_name, page, target, duration_ms, click_location, company, project_title, source, device, is_founder_route, session_id, entry_route, ref_param, metadata',
        { count: 'exact' }
      )
      .order('created_at', { ascending: false })
      .range(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE - 1)

    if (filterType !== 'all') query = query.eq('event_type', filterType)
    if (filterSeen === 'unseen') query = query.eq('seen', false)
    if (filterSeen === 'seen') query = query.eq('seen', true)
    if (filterVisitor) {
      query = applyVisitorEventFilter(query, filterVisitor)
    }

    const { data, count, error } = await query
    if (!error) {
      setEvents(data || [])
      setTotal(count || 0)
    }
    setLoading(false)
  }, [filterType, filterSeen, filterVisitor, page])

  const fetchModalEvents = useCallback(async (visitor) => {
    if (!supabase || !visitor) return
    setModalLoading(true)

    let query = supabase
      .from('analytics_events')
      .select(
        'id, seen, created_at, event_type, visitor_name, page, target, duration_ms, click_location, company, project_title, source, device, language, user_agent, is_founder_route, session_id, metadata'
      )
      .order('created_at', { ascending: false })
      .limit(500)

    query = applyVisitorEventFilter(query, visitor)

    const { data, error } = await query
    if (!error) setModalEvents(data || [])
    setModalLoading(false)
  }, [])

  const modalSessions = useMemo(() => {
    const map = new Map()

    modalEvents.forEach((event) => {
      const sid = event.session_id || 'unknown'
      if (!map.has(sid)) {
        map.set(sid, {
          session_id: sid,
          events: [],
          first_seen: event.created_at,
          last_seen: event.created_at,
          page_views: 0,
          resume_clicks: 0,
          contact_clicks: 0,
          project_opens: 0,
          blog_opens: 0,
          blog_read_ms: 0,
          max_scroll: 0,
          social_clicks: 0,
          identified: false,
          unseen: 0,
          duration_ms: 0,
          device: event.device || null,
          source: event.source || null,
          timezone: metaTimezone(event),
          city: event?.metadata?.city || null,
          region: event?.metadata?.region || null,
          country: event?.metadata?.country || null,
          ip_timezone: event?.metadata?.ip_timezone || null,
          language: event.language || null,
          user_agent: event.user_agent || null,
        })
      }

      const session = map.get(sid)
      session.events.push(event)

      const created = new Date(event.created_at).getTime()
      if (created > new Date(session.last_seen).getTime()) session.last_seen = event.created_at
      if (created < new Date(session.first_seen).getTime()) session.first_seen = event.created_at

      if (event.event_type === 'page_view') session.page_views += 1
      if (event.event_type === 'resume_click') session.resume_clicks += 1
      if (event.event_type === 'contact_click') session.contact_clicks += 1
      if (event.event_type === 'project_open') session.project_opens += 1
      if (event.event_type === 'blog_open') session.blog_opens += 1
      if (event.event_type === 'social_click') session.social_clicks += 1
      if (event.event_type === 'visitor_identified') session.identified = true
      if (!event.seen) session.unseen += 1
      if (event.event_type === 'session_close' && event.duration_ms) {
        session.duration_ms += Number(event.duration_ms) || 0
      }
      if (event.event_type === 'blog_read_time' && event.duration_ms) {
        session.blog_read_ms += Number(event.duration_ms) || 0
      }
      const scrollDepth = event?.metadata?.scroll_depth
      if (typeof scrollDepth === 'number') {
        session.max_scroll = Math.max(session.max_scroll, scrollDepth)
      }
      if (!session.device && event.device) session.device = event.device
      if (!session.source && event.source) session.source = event.source
      if (!session.timezone) session.timezone = metaTimezone(event)
      if (!session.city) session.city = event?.metadata?.city || null
      if (!session.region) session.region = event?.metadata?.region || null
      if (!session.country) session.country = event?.metadata?.country || null
      if (!session.ip_timezone) session.ip_timezone = event?.metadata?.ip_timezone || null
      if (!session.language) session.language = event.language || null
      if (!session.user_agent) session.user_agent = event.user_agent || null
    })

    return Array.from(map.values())
      .map((session) => ({
        ...session,
        events: [...session.events].sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        ),
        total_events: session.events.length,
      }))
      .sort((a, b) => new Date(b.last_seen) - new Date(a.last_seen))
  }, [modalEvents])

  const toggleSession = (sessionId) => {
    setOpenSessionIds((prev) => {
      const next = new Set(prev)
      if (next.has(sessionId)) next.delete(sessionId)
      else next.add(sessionId)
      return next
    })
  }

  useEffect(() => {
    if (authed) {
      fetchVisitors()
      fetchStats()
      fetchFollowups()
    }
  }, [authed, fetchVisitors, fetchStats, fetchFollowups])

  useEffect(() => {
    if (authed && tab === 'events') fetchEvents()
  }, [authed, tab, fetchEvents])

  useEffect(() => {
    setSelectedAnonymousKeys(new Set())
  }, [visitorKind, visitorStatus])

  useEffect(() => {
    if (!eventsModalVisitor) {
      setOpenSessionIds(new Set())
      setModalEvents([])
      return
    }
    setOpenSessionIds(new Set())
    setModalEvents([])
    setAssignVisitorName('')
    fetchModalEvents(eventsModalVisitor)
  }, [eventsModalVisitor, fetchModalEvents])

  useEffect(() => {
    if (!eventsModalVisitor || modalLoading || modalSessions.length === 0) return
    setOpenSessionIds((prev) => {
      if (prev.size > 0) return prev
      return new Set([modalSessions[0].session_id])
    })
  }, [eventsModalVisitor, modalLoading, modalSessions])

  useEffect(() => {
    const modalOpen = Boolean(eventsModalVisitor || checklistVisitor)
    if (!modalOpen) return undefined

    const previousBody = document.body.style.overflow
    const previousHtml = document.documentElement.style.overflow
    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = previousBody
      document.documentElement.style.overflow = previousHtml
    }
  }, [eventsModalVisitor, checklistVisitor])

  const upsertFollowup = async (visitorName, patch) => {
    if (!supabase || !visitorName) return
    const existing = followups[visitorName] || {
      visitor_name: visitorName,
      follow_up_done: false,
      notes: '',
      checklist: DEFAULT_CHECKLIST,
    }
    const next = {
      visitor_name: visitorName,
      follow_up_done: patch.follow_up_done ?? existing.follow_up_done,
      notes: patch.notes ?? existing.notes ?? '',
      checklist: patch.checklist ?? existing.checklist ?? DEFAULT_CHECKLIST,
      updated_at: new Date().toISOString(),
    }

    setFollowups((prev) => ({ ...prev, [visitorName]: next }))

    const { error } = await supabase.from('founder_followups').upsert(next, {
      onConflict: 'visitor_name',
    })
    if (error) console.warn('[followups]', error.message)
  }

  const markSeen = async (ids) => {
    if (!supabase) return
    setMarking(true)
    await supabase.from('analytics_events').update({ seen: true }).in('id', ids)
    setMarking(false)
    fetchEvents()
    fetchStats()
    fetchVisitors()
    if (eventsModalVisitor) fetchModalEvents(eventsModalVisitor)
  }

  const assignAnonymousVisitor = async () => {
    if (!supabase || !eventsModalVisitor || !assignVisitorName) return
    if (!visitors.some((visitor) => visitor.visitor_name === assignVisitorName)) return

    setVisitorActionId(eventsModalVisitor.visitor_key)
    const query = supabase.from('analytics_events').update({ visitor_name: assignVisitorName })
    const { error } = await applyVisitorEventFilter(query, eventsModalVisitor).is('visitor_name', null)
    setVisitorActionId(null)

    if (error) {
      setVisitorError(error.message || 'Could not assign this visitor.')
      return
    }

    setVisitorError('')
    setEventsModalVisitor(null)
    fetchVisitors()
    fetchStats()
    if (tab === 'events') fetchEvents()
  }

  const assignSelectedVisitors = async () => {
    if (!supabase || !bulkFounderName) return
    if (!visitors.some((visitor) => visitor.visitor_name === bulkFounderName)) return
    const selected = filteredVisitors.filter(
      (visitor) => !visitor.visitor_name && selectedAnonymousKeys.has(visitor.visitor_key)
    )
    if (!selected.length) return

    setVisitorActionId('bulk')
    const results = await Promise.all(selected.map((visitor) => {
      const query = supabase.from('analytics_events').update({ visitor_name: bulkFounderName })
      return applyVisitorEventFilter(query, visitor).is('visitor_name', null)
    }))
    setVisitorActionId(null)

    const failed = results.filter(({ error }) => error)
    setVisitorError(failed.length ? `${failed.length} visitor records could not be assigned. Please retry.` : '')
    setSelectedAnonymousKeys(new Set())
    setBulkFounderName('')
    fetchVisitors()
    fetchStats()
    if (tab === 'events') fetchEvents()
  }

  const markVisitorSeen = async (visitor) => {
    if (!supabase || !visitor) return

    setVisitorActionId(visitor.visitor_key)
    let query = supabase.from('analytics_events').update({ seen: true })
    query = applyVisitorEventFilter(query, visitor)
    const { error } = await query
    setVisitorActionId(null)

    if (error) {
      console.warn('[markVisitorSeen]', error.message)
      return
    }

    if (eventsModalVisitor?.visitor_key === visitor.visitor_key) {
      setEventsModalVisitor(null)
    }
    fetchVisitors()
    fetchStats()
    if (tab === 'events') fetchEvents()
  }

  const deleteVisitorRecord = async (visitor) => {
    if (!supabase || !visitor) return

    setVisitorActionId(visitor.visitor_key)
    let query = supabase.from('analytics_events').delete()
    query = applyVisitorEventFilter(query, visitor)
    const { error } = await query

    if (!error && visitor.visitor_name) {
      await supabase.from('founder_followups').delete().eq('visitor_name', visitor.visitor_name)
    }

    setVisitorActionId(null)

    if (error) {
      console.warn('[deleteVisitorRecord]', error.message)
      return
    }

    if (eventsModalVisitor?.visitor_key === visitor.visitor_key) {
      setEventsModalVisitor(null)
    }
    fetchVisitors()
    fetchStats()
    fetchFollowups()
    if (tab === 'events') fetchEvents()
  }

  const markAllVisibleSeen = () => {
    const ids = events.filter((e) => !e.seen).map((e) => e.id)
    if (ids.length) markSeen(ids)
  }

  const openVisitorEvents = (visitor) => {
    setVisitorError('')
    setEventsModalVisitor(visitor)
  }

  const clearVisitorFilter = () => {
    setFilterVisitor(null)
    setPage(0)
  }

  const filteredVisitors = useMemo(() => {
    let rows = visitors
    if (visitorKind === 'founders') rows = rows.filter((v) => v.visitor_name)
    if (visitorKind === 'anonymous') rows = rows.filter((v) => !v.visitor_name)

    if (visitorStatus === 'active') {
      rows = rows.filter((v) => {
        if (v.unseen_events == null && v.all_seen == null) return true
        return Number(v.unseen_events) > 0
      })
    } else if (visitorStatus === 'cleared') {
      rows = rows.filter((v) => {
        if (v.unseen_events == null && v.all_seen == null) return false
        return Number(v.unseen_events) === 0 || v.all_seen === true
      })
    }

    return rows
  }, [visitors, visitorKind, visitorStatus])

  const selectedAnonymousCount = filteredVisitors.filter(
    (visitor) => !visitor.visitor_name && selectedAnonymousKeys.has(visitor.visitor_key)
  ).length

  const toggleAnonymousSelection = (visitorKey) => {
    setSelectedAnonymousKeys((previous) => {
      const next = new Set(previous)
      if (next.has(visitorKey)) next.delete(visitorKey)
      else next.add(visitorKey)
      return next
    })
  }

  const founderFollowupStats = useMemo(() => {
    const founders = visitors.filter((v) => v.visitor_name)
    const done = founders.filter((v) => followups[v.visitor_name]?.follow_up_done).length
    return { total: founders.length, done, pending: founders.length - done }
  }, [visitors, followups])

  const renderEventCell = (row, column) => {
    if (column === 'timezone') {
      const tz = metaTimezone(row)
      return tz ? timezoneLabel(tz) : '—'
    }
    if (column === 'scroll_depth') {
      return metaScrollDepth(row) || '—'
    }

    const value = row[column]
    if (value === null || value === undefined || value === '') return '—'

    if (column === 'event_type') {
      return (
        <span
          className="dash__event-pill"
          style={{
            background: `${EVENT_COLOR[value] || '#64748b'}22`,
            color: EVENT_COLOR[value] || '#94a3b8',
          }}
        >
          {String(value).replace(/_/g, ' ')}
        </span>
      )
    }

    if (column === 'source') return sourceLabel(String(value))
    if (column === 'seen') return value ? '✅' : '🔴'
    if (column === 'is_founder_route') return value ? '✅' : '—'
    if (column === 'created_at') return fmt(value)
    if (column === 'visitor_name') return formatDisplayName(value) || value
    if (column === 'session_id') return `${String(value).slice(0, 12)}…`
    if (column === 'duration_ms') return fmtDuration(value)

    return String(value)
  }

  const checklistFor = (visitorName) =>
    mergeChecklist(followups[visitorName]?.checklist)

  if (!authReady) {
    return (
      <div className="dash-gate">
        <div className="dash-gate__form">
          <p className="dash-gate__title">Checking session…</p>
        </div>
      </div>
    )
  }

  if (!authed) {
    return (
      <div className="dash-gate">
        <form
          className={`dash-gate__form ${authError ? 'dash-gate__form--shake' : ''}`}
          onSubmit={handleLogin}
        >
          <div className="dash-gate__lock">🔒</div>
          <h2 className="dash-gate__title">Analytics Dashboard</h2>
          <input
            type="email"
            className={`dash-gate__input ${authError ? 'dash-gate__input--error' : ''}`}
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            required
            autoFocus
          />
          <input
            type="password"
            className={`dash-gate__input ${authError ? 'dash-gate__input--error' : ''}`}
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
          {authError && <p className="dash-gate__error">{authError}</p>}
          <button type="submit" className="dash-gate__btn" disabled={authLoading}>
            {authLoading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    )
  }

  const eventsModal =
    eventsModalVisitor &&
    createPortal(
      <div
        className="dash-modal"
        role="dialog"
        aria-modal="true"
        data-lenis-prevent
        data-lenis-prevent-wheel
        data-lenis-prevent-touch
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="dash-modal__backdrop"
          aria-label="Close events"
          onClick={() => setEventsModalVisitor(null)}
        />
        <div className="dash-modal__panel dash-modal__panel--wide">
          <header className="dash-modal__header">
            <div>
              <p className="dash-modal__eyebrow">Visitor sessions</p>
              <h2 className="dash-modal__title">
                {eventsModalVisitor.visitor_name
                  ? formatDisplayName(eventsModalVisitor.visitor_name)
                  : 'Anonymous session'}
              </h2>
              <p className="dash-modal__subtitle">
                {modalSessions.length} session{modalSessions.length === 1 ? '' : 's'} ·{' '}
                {modalEvents.length} events · first seen {fmt(eventsModalVisitor.first_seen)} · avg {fmtDuration(eventsModalVisitor.avg_session_ms)}
              </p>
              {!eventsModalVisitor.visitor_name && (
                <p className="dash-modal__subtitle">
                  Timezone is reported by the browser and does not confirm identity.
                </p>
              )}
            </div>
            <div className="dash-modal__actions">
              {!eventsModalVisitor.visitor_name && (
                <div className="dash__assign">
                  <select
                    aria-label="Founder for this anonymous visitor"
                    value={assignVisitorName}
                    onChange={(e) => setAssignVisitorName(e.target.value)}
                  >
                    <option value="">Assign to founder…</option>
                    {visitors.filter((visitor) => visitor.visitor_name).map((visitor) => (
                      <option key={visitor.visitor_name} value={visitor.visitor_name}>
                        {formatDisplayName(visitor.visitor_name)}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="dash__seen-btn"
                    disabled={!assignVisitorName || visitorActionId === eventsModalVisitor.visitor_key}
                    onClick={assignAnonymousVisitor}
                  >
                    Assign
                  </button>
                </div>
              )}
              <button
                type="button"
                className="dash__seen-btn"
                disabled={visitorActionId === eventsModalVisitor.visitor_key}
                onClick={() => markVisitorSeen(eventsModalVisitor)}
              >
                Mark all seen
              </button>
              <button
                type="button"
                className="dash__danger-btn"
                disabled={visitorActionId === eventsModalVisitor.visitor_key}
                onClick={() => deleteVisitorRecord(eventsModalVisitor)}
              >
                Delete record
              </button>
              <button
                type="button"
                className="dash-modal__close"
                onClick={() => setEventsModalVisitor(null)}
              >
                ✕
              </button>
            </div>
          </header>

          <div className="dash-modal__body">
            {visitorError && <p className="dash__visitor-error" role="alert">{visitorError}</p>}
            {modalLoading ? (
              <div className="dash__loading">Loading sessions…</div>
            ) : modalSessions.length === 0 ? (
              <div className="dash__empty">No events for this visitor.</div>
            ) : (
              <div className="dash-session-list">
                {modalSessions.map((session, index) => {
                  const isOpen = openSessionIds.has(session.session_id)
                  return (
                    <section
                      key={session.session_id}
                      className={`dash-session ${isOpen ? 'dash-session--open' : ''}`}
                    >
                      <button
                        type="button"
                        className="dash-session__toggle"
                        aria-expanded={isOpen}
                        onClick={() => toggleSession(session.session_id)}
                      >
                        <span className="dash-session__chevron">{isOpen ? '▾' : '▸'}</span>
                        <div className="dash-session__main">
                          <div className="dash-session__title-row">
                            <strong>Session {modalSessions.length - index}</strong>
                            <span className="dash-session__id">
                              {String(session.session_id).slice(0, 14)}…
                            </span>
                          </div>
                          <div className="dash-session__time">
                            Last activity {fmt(session.last_seen)} · started {fmt(session.first_seen)}
                          </div>
                        </div>
                        <div className="dash-session__stats">
                          <span className="dash-session__stat">
                            <em>{session.total_events}</em> events
                          </span>
                          <span className="dash-session__stat">
                            <em>{session.page_views}</em> views
                          </span>
                          <span
                            className={`dash-session__stat ${session.resume_clicks > 0 ? 'dash-session__stat--ok' : 'dash-session__stat--off'}`}
                          >
                            {session.resume_clicks > 0 ? '✅' : '—'} Resume
                            {session.resume_clicks > 1 ? ` ×${session.resume_clicks}` : ''}
                          </span>
                          <span
                            className={`dash-session__stat ${session.contact_clicks > 0 ? 'dash-session__stat--ok' : 'dash-session__stat--off'}`}
                          >
                            {session.contact_clicks > 0 ? '✅' : '—'} Contact
                          </span>
                          <span
                            className={`dash-session__stat ${session.project_opens > 0 ? 'dash-session__stat--ok' : 'dash-session__stat--off'}`}
                          >
                            {session.project_opens > 0 ? '✅' : '—'} Projects
                            {session.project_opens > 0 ? ` ${session.project_opens}` : ''}
                          </span>
                          <span
                            className={`dash-session__stat ${session.blog_opens > 0 ? 'dash-session__stat--ok' : 'dash-session__stat--off'}`}
                          >
                            {session.blog_opens > 0 ? '✅' : '—'} Blogs
                            {session.blog_opens > 0 ? ` ${session.blog_opens}` : ''}
                          </span>
                          {session.blog_read_ms > 0 && (
                            <span className="dash-session__stat dash-session__stat--ok">
                              <em>{fmtDuration(session.blog_read_ms)}</em> blog read
                              {session.max_scroll > 0 ? ` · ${session.max_scroll}%` : ''}
                            </span>
                          )}
                          <span
                            className={`dash-session__stat ${session.social_clicks > 0 ? 'dash-session__stat--ok' : 'dash-session__stat--off'}`}
                          >
                            {session.social_clicks > 0 ? '✅' : '—'} Social
                          </span>
                          <span className="dash-session__stat">
                            <em>{session.unseen}</em> unseen
                          </span>
                          <span className="dash-session__stat">
                            <em>{fmtDuration(session.duration_ms || null)}</em> time
                          </span>
                          <span className="dash-session__stat dash-session__stat--muted">
                            {session.device || '—'} · {browserLabel(session.user_agent)} · {osLabel(session.user_agent)}
                            {session.language ? ` · ${session.language}` : ''}
                          </span>
                          <span className="dash-session__stat dash-session__stat--muted">
                            {session.source || 'direct'} · {locationLabel(session.city, session.country)}
                            {session.region ? ` · ${session.region}` : ''}
                            {session.timezone ? ` · ${timezoneLabel(session.timezone)}` : ''}
                            {session.ip_timezone && session.ip_timezone !== session.timezone
                              ? ` · IP timezone ${session.ip_timezone}` : ''}
                          </span>
                        </div>
                      </button>

                      {isOpen && (
                        <div className="dash-session__panel">
                          <div className="dash-session__table-wrap">
                            <table className="dash__table dash-session__table">
                              <thead>
                                <tr>
                                  {MODAL_EVENT_COLUMNS.map((col) => (
                                    <th key={col}>{col.replace(/_/g, ' ')}</th>
                                  ))}
                                  <th>Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {session.events.map((e) => (
                                  <tr
                                    key={e.id}
                                    className={e.seen ? 'dash__row--seen' : 'dash__row--unseen'}
                                  >
                                    {MODAL_EVENT_COLUMNS.map((col) => (
                                      <td key={`${e.id}-${col}`} className="dash__td-cell">
                                        {renderEventCell(e, col)}
                                      </td>
                                    ))}
                                    <td>
                                      {!e.seen && (
                                        <button
                                          type="button"
                                          className="dash__seen-btn"
                                          onClick={() => markSeen([e.id])}
                                        >
                                          Mark seen
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </section>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>,
      document.body
    )

  const checklistModal =
    checklistVisitor &&
    createPortal(
      <div
        className="dash-modal dash-modal--sm"
        role="dialog"
        aria-modal="true"
        data-lenis-prevent
        data-lenis-prevent-wheel
        data-lenis-prevent-touch
      >
        <button
          type="button"
          className="dash-modal__backdrop"
          aria-label="Close checklist"
          onClick={() => setChecklistVisitor(null)}
        />
        <div className="dash-modal__panel">
          <header className="dash-modal__header">
            <div>
              <h2 className="dash-modal__title">
                Follow-up · {formatDisplayName(checklistVisitor)}
              </h2>
              <p className="dash-modal__subtitle">Track outreach progress for this founder</p>
            </div>
            <button
              type="button"
              className="dash-modal__close"
              onClick={() => setChecklistVisitor(null)}
            >
              ✕
            </button>
          </header>
          <div className="dash-modal__body">
            <div className="dash__follow-details">
              <label className="dash__follow-check">
                <input
                  type="checkbox"
                  checked={Boolean(followups[checklistVisitor]?.follow_up_done)}
                  onChange={(e) => upsertFollowup(checklistVisitor, { follow_up_done: e.target.checked })}
                />
                <span>Follow-up done</span>
              </label>
              <label className="dash__notes-label" htmlFor="dash-follow-notes">Notes</label>
              <textarea
                id="dash-follow-notes"
                className="dash__notes"
                rows={3}
                placeholder="Follow-up notes…"
                defaultValue={followups[checklistVisitor]?.notes || ''}
                key={`${checklistVisitor}-${followups[checklistVisitor]?.updated_at || 'new'}`}
                onBlur={(e) => {
                  const notes = e.target.value
                  if (notes !== (followups[checklistVisitor]?.notes || '')) {
                    upsertFollowup(checklistVisitor, { notes })
                  }
                }}
              />
            </div>
            <ul className="dash__checklist">
              {checklistFor(checklistVisitor).map((item) => (
                <li key={item.id} className="dash__checklist-item">
                  <label>
                    <input
                      type="checkbox"
                      checked={item.done}
                      onChange={(e) => {
                        const next = checklistFor(checklistVisitor).map((row) =>
                          row.id === item.id ? { ...row, done: e.target.checked } : row
                        )
                        upsertFollowup(checklistVisitor, { checklist: next })
                      }}
                    />
                    <span>{item.label}</span>
                  </label>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>,
      document.body
    )

  return (
    <div className="dash">
      <header className="dash__header">
        <h1 className="dash__heading">📊 Portfolio Analytics</h1>
        <button className="dash__logout" onClick={handleLogout} type="button">
          Logout
        </button>
      </header>

      <ShareLinkBuilder />

      {stats && (
        <section className="dash__stats">
          <div className="dash__stat-card dash__stat-card--blue">
            <span className="dash__stat-value">{stats.total_visitors}</span>
            <span className="dash__stat-label">Visitors</span>
          </div>
          <div className="dash__stat-card dash__stat-card--purple">
            <span className="dash__stat-value">{stats.named_visitors}</span>
            <span className="dash__stat-label">Founders</span>
          </div>
          <div className="dash__stat-card dash__stat-card--slate">
            <span className="dash__stat-value">{stats.anonymous_visitors}</span>
            <span className="dash__stat-label">Anonymous</span>
          </div>
          <div className="dash__stat-card dash__stat-card--red">
            <span className="dash__stat-value">{stats.unseen}</span>
            <span className="dash__stat-label">Unseen</span>
          </div>
          <div className="dash__stat-card dash__stat-card--green">
            <span className="dash__stat-value">{stats.resume_clicks}</span>
            <span className="dash__stat-label">Resume</span>
          </div>
          <div className="dash__stat-card dash__stat-card--yellow">
            <span className="dash__stat-value">{stats.contact_clicks}</span>
            <span className="dash__stat-label">Contact</span>
          </div>
          <div className="dash__stat-card dash__stat-card--orange">
            <span className="dash__stat-value">{stats.project_opens}</span>
            <span className="dash__stat-label">Projects</span>
          </div>
          <div className="dash__stat-card dash__stat-card--teal">
            <span className="dash__stat-value">
              {founderFollowupStats.done}/{founderFollowupStats.total || 0}
            </span>
            <span className="dash__stat-label">Follow-ups done</span>
          </div>
        </section>
      )}

      <div className="dash__tabs">
        <button
          type="button"
          className={`dash__tab ${tab === 'visitors' ? 'dash__tab--active' : ''}`}
          onClick={() => setTab('visitors')}
        >
          Visitors
        </button>
        <button
          type="button"
          className={`dash__tab ${tab === 'events' ? 'dash__tab--active' : ''}`}
          onClick={() => setTab('events')}
        >
          Events
        </button>
      </div>

      {tab === 'visitors' && (
        <>
          <section className="dash__filters">
            <div className="dash__filter-group">
              <label className="dash__filter-label">Visitor type</label>
              <div className="dash__filter-tabs">
                {[
                  ['all', 'All'],
                  ['founders', 'Founders'],
                  ['anonymous', 'Anonymous'],
                ].map(([val, label]) => (
                  <button
                    key={val}
                    type="button"
                    className={`dash__filter-tab ${visitorKind === val ? 'dash__filter-tab--active' : ''}`}
                    onClick={() => setVisitorKind(val)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="dash__filter-group">
              <label className="dash__filter-label">Review status</label>
              <div className="dash__filter-tabs">
                {[
                  ['active', '🔴 Active'],
                  ['cleared', '✅ Cleared'],
                  ['all', 'All records'],
                ].map(([val, label]) => (
                  <button
                    key={val}
                    type="button"
                    className={`dash__filter-tab ${visitorStatus === val ? 'dash__filter-tab--active' : ''}`}
                    onClick={() => setVisitorStatus(val)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </section>

          {visitorError && <p className="dash__visitor-error" role="alert">{visitorError}</p>}

          {selectedAnonymousCount > 0 && (
            <div className="dash__bulk-assign">
              <strong>{selectedAnonymousCount} anonymous {selectedAnonymousCount === 1 ? 'visitor' : 'visitors'} selected</strong>
              <select
                aria-label="Assign selected visitors to founder"
                value={bulkFounderName}
                onChange={(e) => setBulkFounderName(e.target.value)}
              >
                <option value="">Choose founder…</option>
                {visitors.filter((visitor) => visitor.visitor_name).map((visitor) => (
                  <option key={visitor.visitor_name} value={visitor.visitor_name}>
                    {formatDisplayName(visitor.visitor_name)}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="dash__seen-btn"
                disabled={!bulkFounderName || visitorActionId === 'bulk'}
                onClick={assignSelectedVisitors}
              >
                Assign selected
              </button>
              <button
                type="button"
                className="dash__clear-filter"
                onClick={() => setSelectedAnonymousKeys(new Set())}
              >
                Clear selection
              </button>
            </div>
          )}

          <section className="dash__table-wrap">
            {filteredVisitors.length === 0 ? (
              <div className="dash__empty">
                {visitorStatus === 'active'
                  ? 'No active visitors. Cleared records are under the Cleared filter.'
                  : 'No visitors for this filter. Share a /hi/name link to get started.'}
              </div>
            ) : (
              <table className="dash__table dash__table--visitors">
                <thead>
                  <tr>
                    {visitorKind !== 'founders' && <th className="dash__select-col">Select</th>}
                    <th>Visitor</th>
                    <th>Recent activity</th>
                    <th>Sessions</th>
                    <th>Engagement</th>
                    <th>Approx. location</th>
                    <th>Source</th>
                    <th>Unseen</th>
                    {(visitorKind === 'founders' || visitorKind === 'all') && (
                      <th>Follow up</th>
                    )}
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredVisitors.map((v) => {
                    const isFounder = Boolean(v.visitor_name)
                    const fu = isFounder ? followups[v.visitor_name] : null
                    const checklistDone = isFounder
                      ? checklistFor(v.visitor_name).filter((i) => i.done).length
                      : 0

                    return (
                      <tr key={v.visitor_key} className="dash__row--unseen">
                        {visitorKind !== 'founders' && (
                          <td className="dash__select-col">
                            {!isFounder && (
                              <input
                                type="checkbox"
                                aria-label={`Select anonymous visitor last seen ${fmt(v.last_seen)}`}
                                checked={selectedAnonymousKeys.has(v.visitor_key)}
                                onChange={() => toggleAnonymousSelection(v.visitor_key)}
                              />
                            )}
                          </td>
                        )}
                        <td className="dash__visitor-name">
                          {isFounder ? (
                            <strong>{formatDisplayName(v.visitor_name)}</strong>
                          ) : (
                            <span className="dash__anon">Anonymous</span>
                          )}
                          <small title="Reported by the visitor's browser; UTC may be a browser privacy setting">
                            {timezoneLabel(v.timezone)}
                          </small>
                        </td>
                        <td className="dash__recent">
                          <strong>{fmt(v.last_seen)}</strong>
                          <small>Avg {fmtDuration(v.avg_session_ms)}</small>
                        </td>
                        <td>{v.session_count || 1}</td>
                        <td>
                          <div className="dash__engagement">
                            <span>{v.page_views} views</span>
                            {Number(v.project_opens) > 0 && <span>{v.project_opens} projects</span>}
                            {Number(v.resume_clicks) > 0 && <span>Resume {v.resume_clicks}</span>}
                            {Number(v.contact_clicks) > 0 && <span>Contact {v.contact_clicks}</span>}
                          </div>
                        </td>
                        <td className="dash__location" title="Approximate IP location; VPNs and mobile networks may differ from the visitor's actual location">
                          {locationLabel(v.city, v.country)}
                          {v.region && <small>{v.region}</small>}
                        </td>
                        <td className="dash__source">
                          {v.top_source ? sourceLabel(v.top_source) : '—'}
                          {v.used_founder_link && <small>via /hi link</small>}
                        </td>
                        <td>
                          {Number(v.unseen_events) > 0 ? (
                            <span className="dash__unseen-count">{v.unseen_events}</span>
                          ) : (
                            <span className="dash__cleared-tag">Cleared</span>
                          )}
                        </td>
                        {(visitorKind === 'founders' || visitorKind === 'all') && (
                          <td>
                            {isFounder ? (
                              <button
                                type="button"
                                className="dash__seen-btn"
                                onClick={() => setChecklistVisitor(v.visitor_name)}
                              >
                                {fu?.follow_up_done ? 'Done' : 'Todo'} · {checklistDone}/{DEFAULT_CHECKLIST.length}
                              </button>
                            ) : (
                              '—'
                            )}
                          </td>
                        )}
                        <td>
                          <div className="dash__row-actions">
                            <button
                              type="button"
                              className="dash__seen-btn"
                              onClick={() => openVisitorEvents(v)}
                            >
                              View sessions
                            </button>
                            <button
                              type="button"
                              className="dash__seen-btn"
                              disabled={visitorActionId === v.visitor_key || Number(v.unseen_events) === 0}
                              onClick={() => markVisitorSeen(v)}
                            >
                              {visitorActionId === v.visitor_key ? '…' : 'Mark seen'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}

      {tab === 'events' && (
        <>
          {filterVisitor && (
            <div className="dash__visitor-filter">
              <span>
                Showing events for{' '}
                <strong>
                  {filterVisitor.visitor_name
                    ? formatDisplayName(filterVisitor.visitor_name)
                    : 'Anonymous session'}
                </strong>
              </span>
              <button type="button" className="dash__clear-filter" onClick={clearVisitorFilter}>
                Clear filter
              </button>
            </div>
          )}

          <section className="dash__filters">
            <div className="dash__filter-group">
              <label className="dash__filter-label">Event type</label>
              <div className="dash__filter-tabs">
                {FILTER_TYPES.map((t) => (
                  <button
                    key={t}
                    type="button"
                    className={`dash__filter-tab ${filterType === t ? 'dash__filter-tab--active' : ''}`}
                    onClick={() => {
                      setFilterType(t)
                      setPage(0)
                    }}
                  >
                    {t === 'all' ? 'All' : t.replace(/_/g, ' ')}
                  </button>
                ))}
              </div>
            </div>
            <div className="dash__filter-group">
              <label className="dash__filter-label">Status</label>
              <div className="dash__filter-tabs">
                {[
                  ['unseen', '🔴 Unseen'],
                  ['seen', '✅ Seen'],
                  ['all', 'All'],
                ].map(([val, label]) => (
                  <button
                    key={val}
                    type="button"
                    className={`dash__filter-tab ${filterSeen === val ? 'dash__filter-tab--active' : ''}`}
                    onClick={() => {
                      setFilterSeen(val)
                      setPage(0)
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="dash__filter-actions">
              <button
                type="button"
                className="dash__mark-btn"
                onClick={markAllVisibleSeen}
                disabled={marking || events.every((e) => e.seen)}
              >
                {marking ? 'Marking…' : '✅ Mark visible as seen'}
              </button>
            </div>
          </section>

          <section className="dash__table-wrap">
            {loading ? (
              <div className="dash__loading">Loading…</div>
            ) : events.length === 0 ? (
              <div className="dash__empty">No events found for this filter.</div>
            ) : (
              <table className="dash__table">
                <thead>
                  <tr>
                    {EVENT_COLUMNS.map((col) => (
                      <th key={col}>{col.replace(/_/g, ' ')}</th>
                    ))}
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((e) => (
                    <tr key={e.id} className={e.seen ? 'dash__row--seen' : 'dash__row--unseen'}>
                      {EVENT_COLUMNS.map((col) => (
                        <td key={`${e.id}-${col}`} className="dash__td-cell">
                          {renderEventCell(e, col)}
                        </td>
                      ))}
                      <td>
                        {!e.seen && (
                          <button
                            type="button"
                            className="dash__seen-btn"
                            onClick={() => markSeen([e.id])}
                          >
                            Mark seen
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          {total > PAGE_SIZE && (
            <div className="dash__pagination">
              <button
                type="button"
                className="dash__page-btn"
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
              >
                ← Prev
              </button>
              <span className="dash__page-info">
                {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
              </span>
              <button
                type="button"
                className="dash__page-btn"
                disabled={(page + 1) * PAGE_SIZE >= total}
                onClick={() => setPage((p) => p + 1)}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}

      {eventsModal}
      {checklistModal}
    </div>
  )
}
